import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import Button from 'components/_UI/Button'
import Loader from 'components/_UI/Loader'
import Text from 'components/_UI/Text'
import Modal from 'components/_UI/Modal'
import { ArrowLeft, Handshake, PlusSquare } from 'lucide-react'
import { Column } from 'styled/alignment/Column'
import colors from 'theme/colors'
import fonts from 'theme/fonts'
import breakpoints from 'theme/breakpoints'
import SponsorSectionForm from '../sponsors/SponsorSectionForm'
import SponsorSectionCard from '../sponsors/SponsorSectionCard'
import { useEvent } from 'context/events/Event'
import sponsorSectionsApi from 'api/requests/sponsorSections'

export default function SponsorsSection({ data, loading, onBack, toast, onSuccess }) {
    const { event } = useEvent()
    const [modalSection, setModalSection] = useState(false)
    const [deletingSection, setDeletingSection] = useState(false)

    const sections = data || []
    const count = sections.length

    function toggleModalAction(action) {
        setModalSection(action)
    }

    async function deleteSection(id) {
        setDeletingSection(true)
        try {
            await sponsorSectionsApi.delete(event?.slug, id)
            setModalSection(false)
            toast.success('Seção excluída com sucesso!', { position: 'top-center' })
            onSuccess()
        } catch (error) {
        } finally {
            setDeletingSection(false)
        }
    }

    function handleSuccess(item) {
        setModalSection(false)
        if (item === 'createSection') {
            toast.success('Seção criada com sucesso!', { position: 'top-center' })
        }
        if (item === 'editSection') {
            toast.success('Seção atualizada com sucesso!', { position: 'top-center' })
        }
        onSuccess()
    }

    async function moveSection(section, direction) {
        const index = sections.findIndex(s => s.id === section.id)
        const targetIndex = direction === 'up' ? index - 1 : index + 1
        const target = sections[targetIndex]

        if (!target) return

        try {
            await Promise.all([
                sponsorSectionsApi.update(event.slug, section.id, { sort_order: target.sort_order }),
                sponsorSectionsApi.update(event.slug, target.id, { sort_order: section.sort_order }),
            ])
            onSuccess()
        } catch (error) {
        }
    }

    return (
        <Wrapper>
            <BackRow
                onClick={onBack}
                role="button"
                tabIndex={0}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onBack()}
            >
                <BackIconWrap>
                    <ArrowLeft size={18} color={colors.secondaryPurple} />
                </BackIconWrap>
                <BackLabel>Voltar</BackLabel>
            </BackRow>

            <SectionCard>
                <SectionHeader>
                    <HeaderLeft>
                        <IconBadge>
                            <Handshake size={20} color={colors.occasionalPurple} />
                        </IconBadge>
                        <HeaderText>
                            <TitleRow>
                                <SectionTitle>Patrocinadores e apoiadores</SectionTitle>
                                {count > 0 && <CountBadge>{count}</CountBadge>}
                            </TitleRow>
                            <SectionDescription>
                                Divulgue patrocinadores, apoiadores, parceiros e mantenedores do seu evento.
                            </SectionDescription>
                        </HeaderText>
                    </HeaderLeft>

                    <ActionsRow>
                        <Button
                            variant='featured'
                            category='squired'
                            onClick={() => toggleModalAction({ type: 'section' })}
                        >
                            <PlusSquare color={colors.neutralLight} size={16} />
                            <Text
                                color={colors.neutralLight}
                                paddingBottom={0}
                                marginLeft={'6px'}
                            >
                                Adicionar seção
                            </Text>
                        </Button>
                    </ActionsRow>
                </SectionHeader>

                <Divider />

                {loading ? (
                    <LoaderWrap>
                        <Loader />
                    </LoaderWrap>
                ) : count > 0 ? (
                    <ContentArea>
                        {sections.map((section, index) => (
                            <SponsorSectionCard
                                key={section.id}
                                section={section}
                                canMoveUp={index > 0}
                                canMoveDown={index < sections.length - 1}
                                onMoveUp={() => moveSection(section, 'up')}
                                onMoveDown={() => moveSection(section, 'down')}
                                onEditSection={() => toggleModalAction({ type: 'editSection', item: section })}
                                onDeleteSection={() => toggleModalAction({ type: 'deleteSection', item: section })}
                                onSuccess={onSuccess}
                                toast={toast}
                            />
                        ))}
                    </ContentArea>
                ) : (
                    <EmptyState>
                        <EmptyIcon>
                            <Handshake size={28} color={colors.nightLight} />
                        </EmptyIcon>
                        <Text color={colors.night} textAlign='center' paddingBottom={0} name='small'>
                            Nenhuma seção criada ainda.
                        </Text>
                        <EmptyHint>
                            Crie seções como "Patrocinadores" ou "Apoiadores" e cadastre os itens de cada uma.
                        </EmptyHint>
                    </EmptyState>
                )}
            </SectionCard>

            {modalSection?.type === 'deleteSection' && (
                <Modal isOpen={modalSection?.type === 'deleteSection'} onClose={() => setModalSection(false)}>
                    <Text>Tem certeza que deseja excluir essa seção e todos os seus itens?</Text>
                    {deletingSection ? (
                        <Loader />
                    ) : (
                        <ModalActions>
                            <Button onClick={() => setModalSection(false)} variant='light'>Cancelar</Button>
                            <Button onClick={() => deleteSection(modalSection?.item?.id)}>Confirmar</Button>
                        </ModalActions>
                    )}
                </Modal>
            )}

            {(modalSection?.type === 'section' || modalSection?.type === 'editSection') && (
                <SponsorSectionForm
                    modalSection={modalSection}
                    edit={modalSection?.item}
                    setModalSection={setModalSection}
                    onSuccess={handleSuccess}
                />
            )}
        </Wrapper>
    )
}

const slideIn = keyframes`
    from { opacity: 0; transform: translateX(16px); }
    to { opacity: 1; transform: translateX(0); }
`;

const Wrapper = styled.div`
    animation: ${slideIn} 0.25s ease-out;
`;

const BackRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 6px 12px 6px 6px;
    margin-bottom: 16px;
    width: fit-content;
    border-radius: 10px;
    outline: none;
    transition: background 0.15s ease;

    &:hover { background: ${colors.neutralDark}; }
    &:focus-visible { box-shadow: 0 0 0 2px ${colors.secondaryPurple}44; }
`;

const BackIconWrap = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${colors.occasionalPurple}10;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BackLabel = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 500;
    color: ${colors.nightDark};
`;

const SectionCard = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 24px;
    border: 1px solid ${colors.night}11;
    box-shadow: 0 1px 8px ${colors.night}08;
`;

const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 14px;
`;

const IconBadge = styled.div`
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 10px;
    background: ${colors.occasionalPurple}12;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SectionTitle = styled.span`
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const CountBadge = styled.span`
    font-size: ${fonts.sizes.mini};
    font-weight: 700;
    color: ${colors.neutralLight};
    background: ${colors.secondaryPurple};
    padding: 2px 8px;
    border-radius: 20px;
    line-height: 1.4;
`;

const SectionDescription = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    line-height: 1.5;
`;

const ActionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const Divider = styled.div`
    height: 1px;
    background: ${colors.night}11;
    margin: 20px 0;
`;

const LoaderWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    border: 2px dashed ${colors.night}22;
    border-radius: 12px;
    background: ${colors.neutralDark}88;
`;

const EmptyIcon = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: ${colors.night}0A;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
`;

const EmptyHint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
    margin-top: 6px;
    text-align: center;
`;

const ContentArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ModalActions = styled(Column)`
    margin-top: 15px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        gap: 5px;
    }
`;
